import { useEffect, useRef, useState } from "react";
import { Alert, Checkbox, Input, InputNumber, Modal, Progress, Radio, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import { formatBytes } from "./artifact-utils";

const POLL_INTERVAL_MS = 1500;

/** Sunucunun Adoptium'dan (veya verilen adresten) JDK indirmesini başlatır ve ilerlemeyi izler. */
export default function JdkFetchModal({ open, platforms, defaultVersion, onClose, onFinished }) {
    const { t } = useTranslation();

    const [version, setVersion] = useState(defaultVersion);
    const [imageType, setImageType] = useState('jdk');
    const [selected, setSelected] = useState(platforms.map(p => p.key));
    const [customUrl, setCustomUrl] = useState('');
    const [customSha, setCustomSha] = useState('');
    const [job, setJob] = useState(null);
    const timer = useRef(null);

    const running = job && job.state === 'running';
    const customUrlInvalid = !!customUrl && (selected.length !== 1 || !customSha);
    const canStart = !running && selected.length > 0 && !!version && !customUrlInvalid;

    useEffect(() => () => clearTimeout(timer.current), []);

    const poll = async (jobId) => {
        const response = await ApiService.ViewerSettings.GetFetchJob(jobId);
        if (!response?.success) return;

        setJob(response.data);
        if (response.data.state === 'running') {
            timer.current = setTimeout(() => poll(jobId), POLL_INTERVAL_MS);
        } else {
            onFinished();
        }
    };

    const start = async () => {
        const response = await ApiService.ViewerSettings.FetchJdk({
            version,
            platforms: selected,
            imageType,
            customUrl: customUrl || null,
            customSha256: customSha || null
        });
        if (response?.success) {
            setJob({ state: 'running', items: selected.map(p => ({ platform: p, state: 'pending', percent: 0 })) });
            poll(response.data);
        }
    };

    const close = () => {
        clearTimeout(timer.current);
        setJob(null);
        onClose();
    };

    return (
        <Modal
            open={open}
            title={t('Download JDK from the internet')}
            okText={t('Start download')}
            cancelText={job && !running ? t('Close') : t('Cancel')}
            onOk={start}
            onCancel={close}
            okButtonProps={{ disabled: !canStart }}
            maskClosable={!running}
            width={640}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert
                    type="warning"
                    showIcon
                    message={t('The server needs outbound access to api.adoptium.net and github.com. If it is blocked, use the direct URL below or upload the zip manually.')}
                />

                <div>
                    <Typography.Text type="secondary">{t('Feature version')}</Typography.Text>
                    <InputNumber min={8} max={99} value={version} onChange={setVersion} disabled={running} style={{ display: 'block', width: 140 }} />
                </div>

                <div>
                    <Typography.Text type="secondary">{t('Package type')}</Typography.Text>
                    <Radio.Group value={imageType} onChange={(e) => setImageType(e.target.value)} disabled={running} style={{ display: 'block' }}>
                        <Radio value="jdk">{t('JDK (safe default)')}</Radio>
                        <Radio value="jre">{t('JRE (about a quarter of the size, test before rollout)')}</Radio>
                    </Radio.Group>
                </div>

                <div>
                    <Typography.Text type="secondary">{t('Platforms')}</Typography.Text>
                    <Checkbox.Group
                        value={selected}
                        onChange={setSelected}
                        disabled={running}
                        options={platforms.map(p => ({ label: p.displayName, value: p.key }))}
                        style={{ display: 'block' }}
                    />
                    {customUrl && selected.length !== 1 && (
                        <Typography.Text type="danger">
                            {t('Pick exactly one platform when downloading from a direct URL.')}
                        </Typography.Text>
                    )}
                </div>

                <div>
                    <Typography.Text type="secondary">{t('Direct URL (optional, single platform, SHA-256 required)')}</Typography.Text>
                    <Input value={customUrl} onChange={(e) => setCustomUrl(e.target.value)} disabled={running} placeholder="https://…/OpenJDK25U-jdk_x64_windows_hotspot.zip" />
                    <Input value={customSha} onChange={(e) => setCustomSha(e.target.value)} disabled={running} placeholder="sha256" style={{ marginTop: 8 }} />
                </div>

                {job && (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {job.items.map(item => (
                            <div key={item.platform}>
                                <Typography.Text>
                                    {platforms.find(p => p.key === item.platform)?.displayName ?? item.platform}
                                    {' — '}
                                    {t(item.state)}
                                    {item.total > 0 && ` (${formatBytes(item.downloaded)} / ${formatBytes(item.total)})`}
                                </Typography.Text>
                                <Progress
                                    percent={item.percent}
                                    size="small"
                                    status={item.state === 'error' ? 'exception' : item.state === 'done' ? 'success' : 'active'}
                                />
                                {item.error && <Typography.Text type="danger">{item.error}</Typography.Text>}
                            </div>
                        ))}
                    </Space>
                )}
            </Space>
        </Modal>
    );
}
